import { useState, useEffect } from 'react';

const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

export interface Plan {
    id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    popular: boolean;
    monthly: number | null;
    yearly: number | null;
    setupFee: number | null;
    botLimit: number | null;
    messageLimit?: number | null;
    messageOverageRate?: number | null;
    aiChatLimit?: number | null;
    aiOverageRate?: number | null;
    ownWhatsAppNumber?: boolean;
    features: string[];
    contactOnly?: boolean;
    allowedBotTypes?: string[];
    campaignsAllowed?: boolean;
    maxActiveCampaigns?: number | null;
    campaignMaxDays?: number;
    whiteLabel?: boolean;
    apiAccess?: boolean;
}

export interface SetupFees {
    whatsapp?: { own_number?: number };
    facebook?: { we_setup?: number };
}

// Defaults used while loading or if the API is unreachable
export const DEFAULT_PLANS: Plan[] = [
    { id: 'free',       name: 'Free',       description: 'Perfect for testing',           icon: '🎁',  color: '#6b7280', popular: false, monthly: 0,    yearly: 0,    setupFee: 0,    botLimit: 1,    messageLimit: 100,   messageOverageRate: null, aiChatLimit: 0,    aiOverageRate: null, ownWhatsAppNumber: false, features: ['1 Website chatbot', '100 chats/month included', 'No AI chats included', 'Basic Analytics', 'Community Support'],                                                          allowedBotTypes: ['website'],                               campaignsAllowed: false, maxActiveCampaigns: 0,    campaignMaxDays: 0, whiteLabel: false, apiAccess: false },
    { id: 'starter',    name: 'Starter',    description: 'For small businesses',           icon: '🚀',  color: '#6b7280', popular: false, monthly: 45,   yearly: 432,  setupFee: 25,   botLimit: 1,    messageLimit: 500,   messageOverageRate: 0.05, aiChatLimit: 100,  aiOverageRate: 0.10, ownWhatsAppNumber: false, features: ['1 Bot (any channel)', '500 chats/month included — $0.05/extra chat', '100 AI chats included', 'WhatsApp via our shared number', 'Basic Analytics', 'Email Support'], allowedBotTypes: ['website', 'whatsapp', 'facebook', 'voice', 'ai', 'email'], campaignsAllowed: true,  maxActiveCampaigns: 1,    campaignMaxDays: 1, whiteLabel: false, apiAccess: false },
    { id: 'business',   name: 'Business',   description: 'For growing businesses',         icon: '💼',  color: '#5B4FE8', popular: true,  monthly: 75,   yearly: 720,  setupFee: 50,   botLimit: 3,    messageLimit: 2000,  messageOverageRate: 0.05, aiChatLimit: 500,  aiOverageRate: 0.10, ownWhatsAppNumber: true,  features: ['3 Bots (any channel)', '2,000 chats/month included — $0.05/extra chat', '500 AI chats included', 'WhatsApp with your own number', 'Advanced Analytics', 'Priority Support', 'API Access'], allowedBotTypes: ['website', 'whatsapp', 'facebook', 'voice', 'ai', 'email'], campaignsAllowed: true, maxActiveCampaigns: 3, campaignMaxDays: 2, whiteLabel: false, apiAccess: true },
    { id: 'agency',     name: 'Agency',     description: 'For agencies & enterprises',     icon: '🏢',  color: '#3b82f6', popular: false, monthly: 200,  yearly: 1920, setupFee: 100,  botLimit: 10,   messageLimit: 10000, messageOverageRate: 0.05, aiChatLimit: 2000, aiOverageRate: 0.10, ownWhatsAppNumber: true,  features: ['10 Bots (any channel)', '10,000 chats/month included — $0.05/extra chat', '2,000 AI chats included', 'WhatsApp with your own number', 'White Label', 'Dedicated Support', 'Custom Integration'], allowedBotTypes: ['website', 'whatsapp', 'facebook', 'voice', 'ai', 'email'], campaignsAllowed: true, maxActiveCampaigns: null, campaignMaxDays: 7, whiteLabel: true,  apiAccess: true },
    { id: 'enterprise', name: 'Enterprise', description: 'Custom pricing for large teams', icon: '🏗️', color: '#f59e0b', popular: false, monthly: null, yearly: null, setupFee: null, botLimit: null, messageLimit: null,  messageOverageRate: null, aiChatLimit: null, aiOverageRate: null, ownWhatsAppNumber: true,  contactOnly: true, features: ['Unlimited bots', 'Unlimited conversations', 'White label & custom domain', 'Dedicated account manager', 'SLA guarantee', 'Custom integrations'], allowedBotTypes: ['website', 'whatsapp', 'facebook', 'voice', 'ai', 'email'], campaignsAllowed: true, maxActiveCampaigns: null, campaignMaxDays: 7, whiteLabel: true,  apiAccess: true },
];

export const DEFAULT_SETUP_FEES: SetupFees = { whatsapp: { own_number: 20 }, facebook: { we_setup: 50 } };

// Module-level cache so the fetch only fires once per browser session
const _cache: { plans: Plan[] | null; setupFees: SetupFees | null; ts: number } = { plans: null, setupFees: null, ts: 0 };
const CACHE_TTL = 5 * 60 * 1000;

export function usePlans() {
    const [plans, setPlans]       = useState<Plan[]>(DEFAULT_PLANS);
    const [setupFees, setSetupFees] = useState<SetupFees>(DEFAULT_SETUP_FEES);
    const [loading, setLoading]   = useState(true);

    useEffect(() => {
        if (_cache.plans && Date.now() - _cache.ts < CACHE_TTL) {
            setPlans(_cache.plans);
            setSetupFees(_cache.setupFees || DEFAULT_SETUP_FEES);
            setLoading(false);
            return;
        }
        fetch(`${API_BASE}/api/plans`)
            .then(r => r.json())
            .then(data => {
                if (data.success && data.plans) {
                    _cache.plans     = data.plans;
                    _cache.setupFees = data.setupFees || DEFAULT_SETUP_FEES;
                    _cache.ts        = Date.now();
                    setPlans(data.plans);
                    setSetupFees(data.setupFees || DEFAULT_SETUP_FEES);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    // botLimit is null on the plan itself for "unlimited" (e.g. enterprise) —
    // `?? 1` alone can't distinguish that from "plan not found", so it must
    // be checked explicitly rather than folded into one fallback expression,
    // or an unlimited plan silently gets capped at 1 bot instead of ∞.
    const getBotLimit = (planId: string): number => {
        const plan = plans.find(p => p.id === planId);
        if (!plan) return 1;
        return plan.botLimit ?? Infinity;
    };

    const getPrice = (planId: string, cycle: 'monthly' | 'yearly'): number =>
        plans.find(p => p.id === planId)?.[cycle] ?? 0;

    const getSetupFee = (botType: string, mode: string): number => {
        if (botType === 'whatsapp' && mode === 'own_number') return setupFees.whatsapp?.own_number ?? 20;
        if (botType === 'facebook' && mode === 'we_setup') return setupFees.facebook?.we_setup ?? 50;
        return 0;
    };

    return { plans, setupFees, loading, getBotLimit, getPrice, getSetupFee };
}
